import Tooltip from "@material-ui/core/Tooltip";
import {
  Archive,
  BorderColor,
  FolderOpenOutlined,
  InsertDriveFileOutlined,
  Launch,
  Lock,
  MoreVert,
  MoveToInboxOutlined,
  UnarchiveOutlined,
} from "@material-ui/icons";
import api from "api";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import enGB from "date-fns/locale/en-GB";
import Duplicate from "icons/Duplicate";
import DuplicateProject from "pages/Projects/DuplicateProject";
import ProjectMoveForm from "pages/Projects/ProjectMoveForm";
import RenameProject from "pages/Projects/RenameProject";
import { darken } from "polished";
import React, { useState } from "react";
import history from "services/history";
import { useStore } from "store";
import ProjectMinimal from "store/models/ProjectMinimal";
import styled from "styled-components";
import { Toast } from "utils";
import Button from "../Button";
import { Option, OptionButton } from "../OptionButton";

const ProjectDiv = styled.div`
  width: 260px;
  /* Keep cards compact so metadata sits closer to the title. */
  height: 150px;
  padding: 0.625rem 0.625rem 0.5rem 0.938rem;

  background: #ffffff;
  border: 1px solid #cecece;
  box-sizing: border-box;
  border-radius: 4px;
  opacity: 1;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  font-size: 0.875rem;
  font-weight: bold;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  min-height: 0;

  p {
    flex: 0 0 1.1rem;
    height: 1.1rem;
    min-width: 0;
    margin-bottom: 0;

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    margin-top: 0.625rem;
    display: flex;
    font-size: 0.75rem;
    line-height: 0.75rem;
    text-transform: none;
    opacity: 0.4;
    user-select: none;
  }

  &:hover,
  &:focus {
    border-color: #000000;
  }

  &:not(:disabled):active,
  &:not(:disabled):active .btn {
    background-color: ${darken(0.1, "#ffffff")};
  }
`;

const ProjectIcons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.4rem;

  .project-icon {
    width: 44px;
    height: 44px;
  }
`;

const ProjectActions = styled.div`
  display: flex;
  align-items: center;
`;

const ProjectStatusIcons = styled.div`
  align-items: center;
  display: flex;
  gap: 0.375rem;
  height: 1.625rem;
  margin-right: 0.5rem;
`;

const ProjectStatusIcon = styled.div`
  align-items: center;
  color: #4caf50;
  cursor: default;
  display: flex;
  height: 1.625rem;
  justify-content: center;
  width: 1rem;

  svg {
    height: 1rem;
    width: 1rem;
  }
`;

const FolderBadge = styled.div`
  /* Flex-column card layouts stretch direct children unless the badge opts out. */
  align-self: flex-start;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.65rem;
  font-weight: 600;
  color: #4caf50;
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 3px;
  padding: 0.15rem 0.4rem;
  margin-top: 0.35rem;
  text-transform: none;
  box-sizing: border-box;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProjectTimestamps = styled.div`
  flex: 0 0 auto;
  margin-top: 0.25rem;
  min-width: 0;

  span {
    display: block;
    flex: 0 0 auto;
    /* Taller than font size so descenders like "g" are not clipped. */
    height: 1rem;
    line-height: 1rem;
    margin-top: 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span + span {
    margin-top: 0.15rem;
  }
`;

const ProjectLaunchButton = styled(Button)`
  &.btn {
    padding: 0;
    min-width: 1.5rem;
    min-height: 1.625rem;
    margin-right: 0.938rem;

    svg {
      width: 1.25rem;
      height: 1.25rem;
      margin-right: 0;
    }

    &:focus {
      outline: none;
    }
  }

  &.btn-primary {
    border-color: transparent;

    &:focus,
    &:hover,
    &:not(:disabled):active {
      color: #000000;
      background-color: transparent;
      border-color: transparent;
    }
  }
`;

type Props = {
  project: ProjectMinimal;
  urlPrefix: string;
  archived?: boolean;
  folderName?: string;
  onProjectSubmit: () => Promise<void>;
  onArchiveRestore: () => Promise<void>;
};

const ProjectCard: React.FC<Props> = ({
  project,
  urlPrefix,
  archived,
  folderName,
  onProjectSubmit,
  onArchiveRestore,
}) => {
  const store = useStore();
  const { current: currentUser } = store.auth;
  const [renameProjectModalShow, setRenameProjectModalShow] =
    useState<boolean>(false);
  const [moveProjectModalShow, setMoveProjectModalShow] =
    useState<boolean>(false);
  const [duplicateProjectModalShow, setDuplicateProjectModalShow] =
    useState<boolean>(false);

  const handleProjectClick = () => {
    history.push(`${urlPrefix}${project.id}`);
  };

  const handleProjectClickNewTab = (event: any) => {
    event.stopPropagation();
    window.open(`${urlPrefix}${project.id}`, "_blank");
  };

  const handleRestore = async () => {
    try {
      await api.projects.restoreProject(project.id);
    } catch (e) {
      Toast.danger("Project not restored");
      return;
    }
    onArchiveRestore();
    Toast.success("Project restored");
  };

  const handleArchive = async () => {
    try {
      await api.projects.archiveProject(project.id);
    } catch (e) {
      Toast.danger("Project not archived");
      return;
    }
    onArchiveRestore();
    Toast.success("Project archived");
  };

  const projectOptions = archived ? (
    <>
      {currentUser?.isAdmin && (
        <Option onClick={handleRestore}>
          <UnarchiveOutlined />
          Restore Project
        </Option>
      )}
      <Option
        onClick={() => {
          setMoveProjectModalShow(true);
        }}
      >
        <MoveToInboxOutlined />
        Move Project
      </Option>
    </>
  ) : (
    <>
      <Option
        onClick={() => {
          setDuplicateProjectModalShow(true);
        }}
      >
        <Duplicate />
        Duplicate Project
      </Option>
      {currentUser?.isAdmin && (
        <Option onClick={handleArchive}>
          <Archive />
          Archive Project
        </Option>
      )}
      <Option
        onClick={() => {
          setRenameProjectModalShow(true);
        }}
      >
        <BorderColor />
        Rename Project
      </Option>
      <Option
        onClick={() => {
          setMoveProjectModalShow(true);
        }}
      >
        <MoveToInboxOutlined />
        Move Project
      </Option>
    </>
  );

  return (
    <>
      <ProjectDiv
        onClick={handleProjectClick}
      >
        <ProjectIcons>
          <InsertDriveFileOutlined className="project-icon" />
          <ProjectActions>
            <ProjectStatusIcons
              // Status indicators are informational; prevent the card navigation click from receiving them.
              onClick={(event) => event.stopPropagation()}
            >
              {project.isLocked && (
                <Tooltip title="Project Locked">
                  <ProjectStatusIcon>
                    <Lock />
                  </ProjectStatusIcon>
                </Tooltip>
              )}
              {archived && (
                <Tooltip title="Archived Project">
                  <ProjectStatusIcon>
                    <Archive />
                  </ProjectStatusIcon>
                </Tooltip>
              )}
            </ProjectStatusIcons>
            <ProjectLaunchButton onClick={handleProjectClickNewTab}>
              <Launch />
            </ProjectLaunchButton>
            <OptionButton
              id="project-option-dropdown"
              title={<MoreVert />}
              menuAlign="right"
              onClick={(event: any) => {
                event.stopPropagation();
              }}
            >
              {projectOptions}
            </OptionButton>
          </ProjectActions>
        </ProjectIcons>
        <p title={project.name}>{project.name}</p>
        {folderName && (
          <FolderBadge>
            <FolderOpenOutlined style={{ fontSize: "0.75rem" }} />
            {folderName}
          </FolderBadge>
        )}
        <ProjectTimestamps>
          <span
            title={format(parseISO(project.modified), "Pp", { locale: enGB })}
          >
            Last updated {formatDistanceToNow(parseISO(project.modified))} ago
          </span>
          <span
            title={format(parseISO(project.modified), "Pp", { locale: enGB })}
          >
            Created {formatDistanceToNow(parseISO(project.created))} ago
          </span>
        </ProjectTimestamps>
      </ProjectDiv>

      <RenameProject
        instance={project}
        isOpen={renameProjectModalShow}
        setModal={(visible: boolean) => setRenameProjectModalShow(visible)}
        onProjectSubmit={onProjectSubmit}
      />

      <ProjectMoveForm
        archived={archived}
        instance={project}
        isOpen={moveProjectModalShow}
        setModal={(visible: boolean) => setMoveProjectModalShow(visible)}
        onProjectSubmit={onProjectSubmit}
      />
      <DuplicateProject
        instance={project}
        isOpen={duplicateProjectModalShow}
        setModal={(visible: boolean) => setDuplicateProjectModalShow(visible)}
      />
    </>
  );
};

export default ProjectCard;
